import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/fees');

const feeFormSchema = z.object({
  code: z.string().min(1, '商品コードは必須です'),
  name: z.string().min(1, '商品名は必須です'),
  type: z.enum(['EXAM_FEE', 'ADMISSION_FEE', 'TUITION', 'FACILITY_FEE', 'OTHER']),
  amount: z.coerce.number().int().min(0, '金額は0以上である必要があります'),
  applicantCategory: z.enum(['DOMESTIC', 'INTERNATIONAL', 'TRANSFER', 'GRADUATE', 'ADULT', 'ALL']),
  validFrom: z.string().optional(),
  validTo: z.string().optional(),
});

type FeeFormValues = z.infer<typeof feeFormSchema>;

export default function FeeForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<FeeFormValues>({
    resolver: zodResolver(feeFormSchema),
    defaultValues: {
      code: '',
      name: '',
      type: 'EXAM_FEE',
      amount: 0,
      applicantCategory: 'ALL',
      validFrom: '',
      validTo: '',
    },
  });

  const fetchFee = useCallback(
    async (feeId: string) => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/${feeId}`);
        if (res.ok) {
          const data = (await res.json()) as FeeFormValues;
          form.reset({
            code: data.code,
            name: data.name,
            type: data.type,
            amount: data.amount,
            applicantCategory: data.applicantCategory || 'ALL',
            validFrom: data.validFrom || '',
            validTo: data.validTo || '',
          });
        }
      } catch (error) {
        console.error('Failed to fetch fee', error);
      } finally {
        setLoading(false);
      }
    },
    [form],
  );

  useEffect(() => {
    if (isEdit) {
      fetchFee(id);
    }
  }, [id, isEdit, fetchFee]);

  const onSubmit = async (values: FeeFormValues) => {
    try {
      setLoading(true);
      const url = isEdit ? `${API_BASE}/${id}` : API_BASE;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (res.ok) {
        navigate('/admin/enrollment/fees');
      }
    } catch (error) {
      console.error('Failed to save fee', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/enrollment/fees')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {isEdit ? t('modules.fees.editTitle') : t('modules.fees.createTitle')}
          </h1>
          <p className="text-gray-500">{t('modules.fees.description')}</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>{t('modules.fees.basicInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.fees.code')}</FormLabel>
                      <FormControl>
                        <Input placeholder="FEE-001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.fees.type')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="種別を選択" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="EXAM_FEE">{t('modules.fees.types.EXAM_FEE')}</SelectItem>
                          <SelectItem value="ADMISSION_FEE">{t('modules.fees.types.ADMISSION_FEE')}</SelectItem>
                          <SelectItem value="TUITION">{t('modules.fees.types.TUITION')}</SelectItem>
                          <SelectItem value="FACILITY_FEE">{t('modules.fees.types.FACILITY_FEE')}</SelectItem>
                          <SelectItem value="OTHER">{t('modules.fees.types.OTHER')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.fees.name')}</FormLabel>
                    <FormControl>
                      <Input placeholder="一般選抜 検定料" {...field} />
                    </FormControl>
                    <FormDescription>志願者の決済画面に表示される名称です。</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.fees.amount')}</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="applicantCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.fees.category')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="属性を選択" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ALL">{t('modules.fees.categories.ALL')}</SelectItem>
                          <SelectItem value="DOMESTIC">{t('modules.fees.categories.DOMESTIC')}</SelectItem>
                          <SelectItem value="INTERNATIONAL">{t('modules.fees.categories.INTERNATIONAL')}</SelectItem>
                          <SelectItem value="TRANSFER">{t('modules.fees.categories.TRANSFER')}</SelectItem>
                          <SelectItem value="GRADUATE">{t('modules.fees.categories.GRADUATE')}</SelectItem>
                          <SelectItem value="ADULT">{t('modules.fees.categories.ADULT')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('modules.fees.validity')}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="validFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.fees.validFrom')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="validTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.fees.validTo')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-between items-center">
            <Button variant="outline" type="button" onClick={() => navigate('/admin/enrollment/fees')}>
              {t('common.cancel')}
            </Button>
            <div className="flex gap-4">
              {isEdit && (
                <Button variant="destructive" type="button">
                  <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
                </Button>
              )}
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" /> {t('common.save')}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
